import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'

import { sectionOf, type NavTree } from '@/app/registry'
import { useAuthState } from '@/api/auth'
import { TabLinks } from '@/components/ui/Tabs'

import { BannerSlot } from './BannerSlot'
import { BottomNav } from './BottomNav'
import { NavContext } from './navContext'
import { OfflineBanner } from './OfflineBanner'
import { PageHeader } from './PageHeader'
import { Sidebar } from './Sidebar'

/**
 * Оболочка: боковое меню от 1024 px, нижняя панель на телефоне, баннеры, содержимое экрана.
 * Внутри раздела (пункты с parent) заголовок раздела и вкладки рисует оболочка — экран раздела
 * свой PageHeader не рисует. Плавающей плашке тренировки ([data-workout-dock]) main оставляет место снизу.
 * `--bottom-ui` — высота нижних панелей телефона (меню и плашка тренировки): по ней прилипают
 * к низу поле чата, «Сохранить» в редакторах и т. п. (`bottom-(--bottom-ui)`).
 */
export function AppShell({ nav }: { nav: NavTree }) {
  const { pathname } = useLocation()
  const auth = useAuthState()
  const role = auth.data?.user?.role
  const filteredNav = useMemo(() => {
    if (!role || role === 'admin') return nav
    const top = nav.top.filter((item) => item.path !== '/admin')
    const children = Object.fromEntries(Object.entries(nav.children).filter(([key]) => key !== '/admin').map(([key, items]) => [key, items.filter((item) => item.path !== '/settings/backups')]))
    return { ...nav, top, children, tabs: top.filter((item) => item.mobile === 'tab').slice(0, 4), more: top.filter((item) => item.mobile === 'more') }
  }, [role, nav])
  const section = sectionOf(pathname, filteredNav)
  const sectionItem = section ? filteredNav.top.find((i) => i.path === section) : undefined
  const tabs = section ? (filteredNav.children[section] ?? []) : []
  useScrollReset(pathname)

  return (
    <NavContext.Provider value={filteredNav}>
      <div className="group/shell min-h-dvh bg-bg text-fg [--bottom-ui:calc(3.5rem+1px+env(safe-area-inset-bottom))] has-[[data-workout-dock]]:[--bottom-ui:calc(7.75rem+1px+env(safe-area-inset-bottom))] lg:flex lg:[--bottom-ui:0px] lg:has-[[data-workout-dock]]:[--bottom-ui:0px]">
        <Sidebar nav={filteredNav} className="hidden lg:flex" />
        <div className="flex min-w-0 flex-1 flex-col">
          <OfflineBanner />
          <BannerSlot />
          <main className="mx-auto w-full min-w-0 max-w-6xl flex-1 px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-[calc(1rem+env(safe-area-inset-top))] lg:px-8 lg:pb-10 lg:pt-7 group-has-[[data-workout-dock]]/shell:pb-[calc(10rem+env(safe-area-inset-bottom))] lg:group-has-[[data-workout-dock]]/shell:pb-28">
            {section && tabs.length > 0 ? (
              <>
                <PageHeader title={sectionItem?.label ?? ''} className="lg:mb-3" />
                <TabLinks items={tabs} className="mb-5" />
              </>
            ) : null}
            <Outlet />
          </main>
        </div>
        <BottomNav nav={filteredNav} className="lg:hidden" />
      </div>
    </NavContext.Provider>
  )
}

/** Новый экран открывается сверху; «Назад» (POP) прокрутку не трогает — браузер вернёт, где был. */
function useScrollReset(pathname: string): void {
  const navigation = useNavigationType()
  useEffect(() => {
    if (navigation !== 'POP') window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене адреса
  }, [pathname])
}
